"use client";

import { useEffect, useState, useMemo } from "react";
import Link from "next/link";
import { ArticleContentParser } from "./ArticleContentParser";
import type { BlogPostItem } from "./BlogListClient";

interface MegaArticleClientProps {
  post: {
    id: string;
    title: string;
    slug: string;
    body: string | null;
    excerpt: string | null;
    coverImage: string | null;
    publishedAt: Date | string | null;
    readingTime: number | null;
    authorName?: string | null;
    author?: { name: string | null } | null;
    category?: { name: string; slug: string } | null;
  };
  relatedPosts: BlogPostItem[];
}

export interface TocItem {
  id: string;
  text: string;
  level: number;
}

function fmtDate(d: Date | string | null) {
  if (!d) return "";
  const dateObj = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(dateObj);
}

export function MegaArticleClient({ post, relatedPosts }: MegaArticleClientProps) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeTocId, setActiveTocId] = useState<string>("");
  const [copiedLink, setCopiedLink] = useState(false);
  const [fontSizeLevel, setFontSizeLevel] = useState<"sm" | "md" | "lg">("md");
  const [isZenMode, setIsZenMode] = useState(false);
  const [isTocMobileOpen, setIsTocMobileOpen] = useState(false);
  const [userVote, setUserVote] = useState<"yes" | "no" | null>(null);
  const [helpfulCounts, setHelpfulCounts] = useState({ yes: 142, no: 3 });

  // 1. Parse Table of Contents (TOC) directly from body string
  const tocItems = useMemo(() => {
    if (!post.body) return [];
    const items: TocItem[] = [];
    const headingRegex = /<h([23])>(.*?)<\/h[23]>/gi;
    let match;
    let idx = 0;

    while ((match = headingRegex.exec(post.body)) !== null) {
      const level = parseInt(match[1], 10);
      const text = match[2].replace(/<[^>]*>/g, "").trim();
      if (!text) continue;

      const slugId = `section-${idx}-${text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")}`;
      items.push({ id: slugId, text, level });
      idx++;
    }
    return items;
  }, [post.body]);

  // 2. Reading Progress Bar & Active Section Highlight
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const currentProgress = (window.scrollY / totalHeight) * 100;
        setScrollProgress(Math.min(100, Math.max(0, currentProgress)));
      }

      if (tocItems.length > 0) {
        let currentActiveId = "";
        for (const item of tocItems) {
          const elem = document.getElementById(item.id);
          if (elem) {
            const rect = elem.getBoundingClientRect();
            if (rect.top <= 160) {
              currentActiveId = item.id;
            }
          }
        }
        if (currentActiveId) setActiveTocId(currentActiveId);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tocItems]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = post.title;

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    });
  };

  const handleVote = (type: "yes" | "no") => {
    if (userVote) return;
    setUserVote(type);
    setHelpfulCounts((prev) => ({
      ...prev,
      [type]: prev[type] + 1,
    }));
  };

  const wordCount = useMemo(() => {
    if (!post.body) return 0;
    const text = post.body.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return text.split(" ").filter(Boolean).length;
  }, [post.body]);

  return (
    <div className={`mega-article-container ${isZenMode ? "mega-article--zen" : ""} font-size-${fontSizeLevel}`}>
      {/* 1. Barra de Progresso de Leitura Fixa no Topo */}
      <div className="reading-progress-bar" style={{ width: `${scrollProgress}%` }} />

      {/* 2. Reader Toolbar */}
      <div className="article-reader-toolbar">
        <div className="reader-toolbar-left">
          <button
            type="button"
            className={`reader-tool-btn ${isZenMode ? "reader-tool-btn--active" : ""}`}
            onClick={() => setIsZenMode(!isZenMode)}
            title="Modo Leitura Zen sem Distrações"
          >
            {isZenMode ? "👁️ Sair do Modo Zen" : "📖 Modo Leitura Zen"}
          </button>

          <div className="font-size-picker">
            <span className="font-picker-label">Fonte:</span>
            <button
              type="button"
              className={`font-btn ${fontSizeLevel === "sm" ? "active" : ""}`}
              onClick={() => setFontSizeLevel("sm")}
            >
              A-
            </button>
            <button
              type="button"
              className={`font-btn ${fontSizeLevel === "md" ? "active" : ""}`}
              onClick={() => setFontSizeLevel("md")}
            >
              A
            </button>
            <button
              type="button"
              className={`font-btn ${fontSizeLevel === "lg" ? "active" : ""}`}
              onClick={() => setFontSizeLevel("lg")}
            >
              A+
            </button>
          </div>
        </div>

        <div className="reader-toolbar-right">
          <a
            href={`/api/blog/${post.slug}/txt`}
            target="_blank"
            rel="noopener noreferrer"
            className="reader-tool-btn reader-tool-btn--ai"
            title="Baixar Texto Puro para Ingestão em ChatGPT, Claude ou Perplexity"
          >
            🤖 Texto Puro para IA (.txt)
          </a>
          <button
            type="button"
            className="reader-tool-btn"
            onClick={() => window.print()}
            title="Imprimir ou Salvar Artigo em PDF"
          >
            🖨️ Imprimir / PDF
          </button>
        </div>
      </div>

      {/* Grid Principal: Sumário (TOC) + Conteúdo */}
      <div className="article-content-layout">
        {/* Sumário Lateral Flutuante (TOC) */}
        {tocItems.length > 0 && (
          <aside className="article-toc-sidebar">
            <div className={`article-toc-card ${isTocMobileOpen ? "toc-mobile--open" : ""}`}>
              <button
                type="button"
                className="toc-header"
                onClick={() => setIsTocMobileOpen(!isTocMobileOpen)}
                aria-expanded={isTocMobileOpen}
              >
                <div className="toc-header-left">
                  <span className="toc-icon">📑</span>
                  <span className="toc-title">Índice do Artigo</span>
                  <span className="toc-badge">{tocItems.length} tópicos</span>
                </div>
                <span className="toc-toggle-badge">{isTocMobileOpen ? "▴ Ocultar" : "▾ Expandir"}</span>
              </button>
              <nav className="toc-list">
                {tocItems.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={`toc-link ${item.level === 3 ? "toc-link--sub" : ""} ${
                      activeTocId === item.id ? "toc-link--active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      const target = document.getElementById(item.id);
                      if (target) {
                        target.scrollIntoView({ behavior: "smooth", block: "start" });
                        setActiveTocId(item.id);
                      }
                    }}
                  >
                    {item.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* Conteúdo Principal da Leitura */}
        <main className="article-main-body">
          {/* Badge Telemetria de Leitura */}
          <div className="article-telemetry-bar">
            <span>📊 {wordCount} palavras</span>
            <span>⏱️ {post.readingTime ?? Math.ceil(wordCount / 180)} min de leitura</span>
            <span>📂 {post.category?.name ?? "Pauta Fiscal"}</span>
          </div>

          {/* Parser de Conteúdo em React Nativo (CodeBlocks + FAQ Accordion + HTML) */}
          <ArticleContentParser html={post.body ?? "<p>Conteúdo em preparação.</p>"} slug={post.slug} />

          {/* Widget de Avaliação de Utilidade */}
          <div className="article-feedback-widget">
            <div className="feedback-question">
              <h4>Este artigo foi útil para o seu trabalho?</h4>
              <p>Sua avaliação nos ajuda a produzir conteúdos técnicos ainda melhores.</p>
            </div>
            <div className="feedback-actions">
              <button
                type="button"
                className={`feedback-btn ${userVote === "yes" ? "feedback-btn--voted" : ""}`}
                onClick={() => handleVote("yes")}
              >
                👍 Sim ({helpfulCounts.yes})
              </button>
              <button
                type="button"
                className={`feedback-btn ${userVote === "no" ? "feedback-btn--voted" : ""}`}
                onClick={() => handleVote("no")}
              >
                👎 Não ({helpfulCounts.no})
              </button>
            </div>
            {userVote && (
              <p className="feedback-thanks">Obrigado pelo seu feedback! 🚀</p>
            )}
          </div>

          {/* Barra de Compartilhamento Social */}
          <div className="article-share-bar">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="mono" style={{ fontSize: 12, color: "var(--neon-aqua)", letterSpacing: "0.05em" }}>
                COMPARTILHAR:
              </span>
              <span style={{ fontSize: 14, color: "#aab9d8" }}>Envie este artigo para sua equipe de TI / Fiscal</span>
            </div>
            <div className="article-share-buttons">
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle}\n${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn share-btn--whatsapp"
                title="Compartilhar no WhatsApp"
              >
                📱 WhatsApp
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn share-btn--linkedin"
                title="Compartilhar no LinkedIn"
              >
                💼 LinkedIn
              </a>
              <button
                type="button"
                onClick={handleCopyShareLink}
                className={`share-btn ${copiedLink ? "code-copy-btn--copied" : ""}`}
              >
                {copiedLink ? "✓ Link Copiado!" : "🔗 Copiar Link"}
              </button>
            </div>
          </div>

          {/* Card do Autor Institucional / Consultoria */}
          <div className="article-author-box">
            <div className="article-author-avatar">ERP</div>
            <div className="article-author-info">
              <h4>{post.author?.name ?? post.authorName ?? "Equipe Técnica Global ERP"}</h4>
              <p>
                Consultoria especialista independente em TOTVS Protheus®, Fluig®, Inteligência de Dados e Integrações
                Corporativas. Ajudamos médias e grandes empresas a extraírem a máxima performance e conformidade fiscal de seu ERP.
              </p>
            </div>
          </div>

          {/* Seção de Artigos Relacionados */}
          {relatedPosts.length > 0 && (
            <section className="related-posts-section">
              <h3 className="related-posts-title">
                <span style={{ color: "var(--neon-aqua)" }}>◈</span> Artigos Relacionados
              </h3>
              <div className="blog-grid">
                {relatedPosts.slice(0, 3).map((item) => (
                  <Link className="blog-card reveal-tilt" href={`/blog/${item.slug}`} key={item.id}>
                    {item.coverImage && (
                      <div className="blog-card-img-wrap">
                        <img
                          src={item.coverImage}
                          alt={item.title}
                          className="blog-card-img"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.onerror = null;
                            target.src = "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80";
                          }}
                        />
                      </div>
                    )}
                    <div className="blog-card__top">
                      <span className="blog-card__cat">
                        {item.category?.name ?? "Pauta Fiscal"}
                      </span>
                      <span className="blog-card__date">
                        {fmtDate(item.publishedAt).toUpperCase()}
                      </span>
                    </div>
                    <h4 className="blog-card__title" style={{ fontSize: 17 }}>
                      {item.title}
                    </h4>
                    <div className="blog-card__foot">
                      <span>{item.readingTime ? `${item.readingTime} min de leitura` : "Leitura rápida"}</span>
                      <span className="blog-card__arrow">→</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
